import { apiClient, type ApiCreateMessageRequest, type MessagesResponse } from './api';

// Chat API service
export const chatApi = {
  /**
   * Send a message to create a new message in the chat
   * @param message - The text message content
   * @param chatId - The chat ID (null for new chats)
   * @param id - Unique message ID
   * @param accessToken - Optional access token for authentication
   * @param attachments - Array of file attachments
   * @param requestType - Type of request:
   *   - "text_to_image": Text-only prompts (default)
   *   - "image_to_image": Single image upload
   *   - "images_to_image": Multiple images uploaded
   *   - "inpainting": Inpainting mode (original image + mask, no aspect ratio sent)
   *   - "image_to_text": Future support for image analysis
   * @param aspectRatio - Desired aspect ratio (default: "3:4", ignored for inpainting)
   */
  async sendMessage(
    message: string, 
    chatId: number | null, 
    id: string, 
    accessToken?: string,
    attachments?: Array<{ Type: number; ContentType: string; Content: string; }>,
    requestType?: string,
    aspectRatio?: string
  ): Promise<MessagesResponse> {
    console.log('🚀 Sending message to API:', { 
      message, 
      chatId, 
      id: id, 
      hasToken: !!accessToken,
      attachmentCount: attachments?.length ?? 0
    });
    
    console.log('🔍 DEBUG: Raw attachments param:', attachments);
    console.log('🔍 DEBUG: Attachments is array?', Array.isArray(attachments));
    console.log('🔍 DEBUG: Attachments length:', attachments?.length);
    
    // Build metadata conditionally - exclude aspect_ratio for inpainting
    const metadata: Record<string, string> = {
      "request_type": requestType ?? "text_to_image"
    };
    
    // Only add aspect_ratio if not inpainting
    if (requestType !== "inpainting") {
      metadata["aspect_ratio"] = aspectRatio ?? "3:4";
    }

    const messageData: ApiCreateMessageRequest = {
      Id: id,  // Fixed: use 'id' parameter instead of undefined 'messageId'
      Message: message,
      ...(chatId && { ChatId: chatId }), // Only include ChatId if it exists, as number
      Attachments: attachments ?? [], // Use provided attachments or empty array
      Metadata: metadata,
    };
    
    console.log('📤 Message data being sent to API:', JSON.stringify(messageData, null, 2));
    console.log('📎 Final attachments in payload:', messageData.Attachments?.length ?? 0);
    return apiClient.createMessage(messageData, accessToken);
  },
};
